import { PolicyLayout } from '../components/PolicyLayout';
import { ContactBlock } from '../components/ContactBlock';

const SECTIONS = [
  { id: 'commitment', title: 'Our Commitment' },
  { id: 'standards', title: 'Accessibility Standards' },
  { id: 'features', title: 'Current Accessibility Features' },
  { id: 'known-limitations', title: 'Known Limitations' },
  { id: 'assistive-technology', title: 'Assistive Technology Compatibility' },
  { id: 'feedback', title: 'Feedback and Contact' },
  { id: 'enforcement', title: 'Enforcement Procedure' },
  { id: 'review', title: 'Review and Updates' },
];

export const AccessibilityStatement = () => {
  return (
    <PolicyLayout title="Accessibility Statement" lastUpdated="22 April 2026" sections={SECTIONS}>
      <section id="commitment">
        <h2>Our Commitment</h2>
        <ul>
          <li>
            NursingCareBooking.com is committed to ensuring digital accessibility for people of all
            abilities.
          </li>
          <li>
            We believe that everyone should be able to access care services, and our Platform should
            not be a barrier to finding and booking quality care.
          </li>
          <li>
            We are continually improving the user experience for everyone and applying the relevant
            accessibility standards.
          </li>
          <li>
            Accessibility is an ongoing effort, and we are dedicated to making our Platform as
            inclusive as possible.
          </li>
        </ul>
      </section>

      <section id="standards">
        <h2>Accessibility Standards</h2>
        <ul>
          <li>
            We aim to conform to the Web Content Accessibility Guidelines (WCAG) 2.1 at Level AA.
          </li>
          <li>
            These guidelines explain how to make web content more accessible for people with
            disabilities.
          </li>
          <li>
            Conforming to these guidelines also helps make content more accessible to older
            individuals, people with changing abilities due to ageing, and all users generally.
          </li>
          <li>
            We regularly audit our Platform against these standards and work to address any issues
            identified.
          </li>
        </ul>
      </section>

      <section id="features">
        <h2>Current Accessibility Features</h2>
        <p>Our Platform includes the following accessibility features:</p>
        <ul>
          <li>
            <strong>Responsive Design:</strong> The Platform is fully responsive and works across
            devices of all sizes, from mobile phones to desktop computers.
          </li>
          <li>
            <strong>Keyboard Navigation:</strong> Core functionality can be accessed using keyboard
            navigation.
          </li>
          <li>
            <strong>Semantic HTML:</strong> We use semantic HTML elements to provide meaningful
            structure for screen readers and assistive technologies.
          </li>
          <li>
            <strong>Form Labels:</strong> Form fields include descriptive labels and clear error
            messages.
          </li>
          <li>
            <strong>Colour Contrast:</strong> We aim to maintain sufficient colour contrast ratios
            between text and background colours throughout the Platform.
          </li>
          <li>
            <strong>Text Sizing:</strong> Text can be resized up to 200% without loss of content or
            functionality using browser zoom.
          </li>
          <li>
            <strong>Focus Indicators:</strong> Interactive elements display visible focus indicators
            for keyboard users.
          </li>
          <li>
            <strong>Alternative Text:</strong> Images include descriptive alternative text where
            appropriate.
          </li>
          <li>
            <strong>Clear Navigation:</strong> Consistent navigation structure across all pages.
          </li>
          <li>
            <strong>Error Identification:</strong> Form validation errors are clearly identified and
            described in text.
          </li>
        </ul>
      </section>

      <section id="known-limitations">
        <h2>Known Limitations</h2>
        <p>Despite our efforts, some areas of the Platform may have limitations:</p>
        <ul>
          <li>
            <strong>Map and Location Features:</strong> Geographic location features using browser
            geolocation may have limited accessibility for some assistive technologies.
          </li>
          <li>
            <strong>Third-Party Content:</strong> Some third-party integrations (Google Analytics,
            Meta Pixel) may have their own accessibility limitations outside our control.
          </li>
          <li>
            <strong>Image Gallery:</strong> Provider profile image galleries may have limited
            keyboard navigation on some devices.
          </li>
          <li>
            <strong>Real-Time Messaging:</strong> The in-app messaging system may have limited
            compatibility with some screen readers.
          </li>
        </ul>
        <ul>
          <li>
            We are actively working to address these limitations and welcome reports of any
            accessibility barriers you encounter.
          </li>
        </ul>
      </section>

      <section id="assistive-technology">
        <h2>Assistive Technology Compatibility</h2>
        <p>Our Platform is designed to be compatible with:</p>
        <ul>
          <li>Screen readers (including NVDA, JAWS, and VoiceOver)</li>
          <li>Screen magnification software</li>
          <li>Speech recognition software</li>
          <li>Keyboard-only navigation</li>
          <li>Browser accessibility extensions</li>
        </ul>
        <ul>
          <li>
            If you experience issues with a specific assistive technology, please let us know so we
            can investigate and improve compatibility.
          </li>
        </ul>
      </section>

      <section id="feedback">
        <h2>Feedback and Contact</h2>
        <ContactBlock
          introText="We welcome your feedback on the accessibility of NursingCareBooking.com. Please let us know if you encounter accessibility barriers:"
          extraContent={
            <>
              <p>We aim to respond to accessibility feedback within 5 working days.</p>
              <p>
                If you are unable to access any content or feature of the Platform, please contact us
                and we will do our best to provide the information in an alternative format.
              </p>
            </>
          }
        />
      </section>

      <section id="enforcement">
        <h2>Enforcement Procedure</h2>
        <p>
          If you are not satisfied with our response to your accessibility concern, you may contact
          the Equality Advisory Support Service (EASS).
        </p>
        <p>
          EASS provides advice and information to individuals on equality and human rights issues.
        </p>
        <ul>
          <li>
            <strong>Website:</strong> equalityadvisoryservice.com
          </li>
          <li>
            <strong>Telephone:</strong> 0808 800 0082
          </li>
        </ul>
        <ul>
          <li>
            You may also contact the Equality and Human Rights Commission (EHRC) at
            equalityhumanrights.com.
          </li>
        </ul>
      </section>

      <section id="review">
        <h2>Review and Updates</h2>
        <ul>
          <li>This Accessibility Statement was last reviewed on 22 April 2026.</li>
          <li>
            We review this statement annually and whenever significant changes are made to the
            Platform.
          </li>
          <li>
            We conduct regular accessibility audits and user testing to identify and address issues.
          </li>
          <li>
            Our goal is to achieve and maintain WCAG 2.1 Level AA conformance across the entire
            Platform.
          </li>
        </ul>
      </section>
    </PolicyLayout>
  );
};
